import { TransformType } from 'new/components/ReusableTransform/types';

export type PrimitiveTypes = string | number | boolean;

export type ArchivalStatus = 'LIVE' | 'ARCHIVED' | 'RESTORING';

export enum ACCESS_ROLES {
  ADMIN = 'admin',
  OWNER = 'owner',
  COLLABORATOR = 'collaborator',
}

export interface Form<T> {
  awaitingValidation: false;
  currentStage: 0;
  dialogIsOpen: false;
  error: null;
  isActive: true;
  isComplete: false;
  data: T;
}

export interface MetaInfo {
  displayName: string;
  icon: string;
  vendorId: number;
  iconSm: string;
  connectionCategory: string;
  industryCategory: string;
  key: string;
  value: string;
  label: string;
  name: string;
  allowSinkAutoMapping?: boolean;
}

export interface ConnectionAuth {
  fetchAuthParams: any;
  items: Array<Record<string, any>>;
  templates: Array<any>;
}

export interface ApiKey {
  api_key: string;
  id: number;
  name: string;
  description: string;
  status: string; // todo: update to all type of status
  url: string;
  owner_id?: number;
  org_id?: number;
  data_source_id?: number;
  last_rotated_key?: string;
  last_rotated_at?: string;
  updated_at?: string;
  created_at?: string;
  resourceType?: string;
  resourceId?: number;
}

export type ApiKeysMap = Record<string, ApiKey>;

export interface ServiceKey {
  api_key: string;
  created_at: string;
  description: string;
  id: number;
  last_rotated_at: string | null;
  last_rotated_key: string | null;
  name: string;
  org_id: number;
  owner_id: number;
  status: string;
  updated_at: string;
}

export interface SdkApplication {
  id: number;
  uuid: string;
  name: string;
  description: string;
  status: string;
  allowed_origins: string[];
  owner_id: number;
  org_id: number;
  created_at: string;
  updated_at: string;
  /** Only present on create response — the auto-provisioned service key */
  service_key?: {
    id: number;
    name: string;
    api_key: string;
    status: string;
    created_at: string;
  };
}

type Config = Record<string, number | string | Array<any>>;
export type SourceConfig = Config;
export type SinkConfig = Config;
export type ApiConfig = Config;
export type TemplatizedAPIConfig = Config;

export type Field = {
  default?: string;
  defaultValue?: string | boolean;
  name: string;
  label?: string;
  display_name: string;
  options?: Array<{ label: string; value: string }>;
  values?: string[];
  description?: string;
  inputType: string;
  required: boolean;
  variant?: string;
  key: number;
  section: string;
  type: string;
  dependentsLayout?: string;
  renderAside?: Field[];
};

export type NoSQLCollection = {
  database: string;
  id: string;
  name: string;
  scanned: boolean;
};

export type DataBaseTable = {
  columns: Array<Record<string, any>>;
  database: string;
  id: string;
  'insert.mode': string;
  name: string;
  'primary.key': string | number | undefined;
};

/**
 * Vendor Types
 */
export interface Vendor {
  auth_templates: any[];
  config: {
    isSource: boolean;
    isSink: boolean;
    connectionCategory: string;
    info_url: string;
    industryCategory: string;
  };
  connection_type: string;
  created_at: string;
  description: string;
  display_name: string;
  id: number;
  logo: string;
  name: string;
  small_logo: string;
  updated_at: string;
}

/**
 * Reusable Transform Types
 */
export interface ReusableTransformType {
  access_roles: string[];
  code: Record<number, any>;
  codeLanguage: string;
  codeConfig: {};
  code_encoding: string;
  code_type: string;
  copied_from_idd: null;
  created_at: string;
  custom_config: {};
  data_credentials_id: null;
  data_sets: Record<number, any>;
  decoded: string;
  description: string;
  id: number;
  managed: boolean;
  name: string;
  org: number;
  output_type: TransformType;
  owner: number;
  public: boolean;
  resource_type: TransformType;
  reusable: boolean;
  runtime_data_credentials_id: null;
  tags: string[];
  updatedAt: string;
}

export interface ReusableTransformDocumentationType {
  access_roles: string[];
  copied_from_id?: number;
  created_at: string;
  description: string;
  doc_type: string;
  id: number;
  name: string;
  org: number;
  owner: number;
  public: boolean;
  repo_config: Record<string, unknown>;
  repo_type: string;
  tags: string[];
  text: string;
  update_aAt: string;
}

export interface CatalogConfig {
  id: number;
  owner: number;
  org: number;
  data_credentials: number;
  name: string;
  description: string | null;
  status: string;
  config: {
    schema_id: string;
    relation_id: string;
  };
  templates: Record<any, any>;
  mode: string;
  job_id: number | null;
  updated_at: string;
  created_at: string;
}

export interface Credential {
  id: number;
  name: string;
  description: string | null;
  owner: number;
  org: number;
  access_roles: string[];
  credentials_version: string;
  managed: boolean;
  credentials_type: string;
  connector: {
    id: number;
    type: string;
    connection_type: string;
    name: string;
    description: string;
    nexset_api_compatible: boolean;
  };
  api_keys: [];
  credentials_non_secure_data: {};
  tags: [];
  verified_status: string;
  verified_at: string;
  copied_from_id: number | null;
  updated_at: string;
  created_at: string;
  validStatus: string;
  _vendorConnectionType?: string;
}

/**
 * Org
 */
export enum ORGANIZATION_STATUS {
  ACTIVE = 'ACTIVE',
  DEACTIVATED = 'DEACTIVATED',
  SOURCE_COUNT_CAPPED = 'SOURCE_COUNT_CAPPED',
  SOURCE_DATA_CAPPED = 'SOURCE_DATA_CAPPED',
  TRIAL_EXPIRED = 'TRIAL_EXPIRED',
}

export enum ORG_TIER_NAME_OPTIONS {
  FREE = 'FREE',
  TRIAL = 'TRIAL',
  FREE_FOREVER = 'FREE_FOREVER',
  SELF_SIGNUP_TRIAL = 'SELF_SIGNUP_TRIAL',
}

export const TrialPlanMessageMap = Object.freeze({
  [ORG_TIER_NAME_OPTIONS.TRIAL]: 'Trial Plan',
  [ORG_TIER_NAME_OPTIONS.SELF_SIGNUP_TRIAL]: 'Trial Plan',
  [ORGANIZATION_STATUS.TRIAL_EXPIRED]: 'Trial Expired',
  [ORGANIZATION_STATUS.SOURCE_COUNT_CAPPED]: 'Quota Limit Exceeded',
  [ORGANIZATION_STATUS.SOURCE_DATA_CAPPED]: 'Quota Limit Exceeded',
});

export interface OrgTier {
  data_source_count_limit: number;
  display_name: string;
  id: number;
  name: ORG_TIER_NAME_OPTIONS;
  record_count_limit: number;
  record_count_limit_time: string;
  trial_period_days: number;
}

export interface Organization {
  client_identifier: null;
  cluster_id: number;
  cluster_status: string;
  email: string | null;
  email_domain: string;
  id: number;
  name: string;
  new_cluster_id: null | number;
  org_tier?: OrgTier;
  org_webhook_host: string;
  owner: number;
  self_signup: boolean;
  status: ORGANIZATION_STATUS;
  features_enabled: string | string[] | null;
}

interface TriggerEvent {
  id: number;
  owner: any;
  org: Organization;
  status: string;
  triggering_event_type: string;
  triggering_origin_node_id: number;
  triggering_flow_node_id: number;
  triggering_resource_type: string;
  triggering_resource_id: number;
  triggered_event_type: string;
  triggered_origin_node_id: number;
  triggered_resource_type: string;
  triggered_resource_id: number;
  updated_at: string; // ISO 8601 date string
  created_at: string; // ISO 8601 date string
}

interface IResource {
  id: number;
  origin_node_id: number;
  flow_node_id: number;
  name: string;
  description: string | null;
  status: string;
  runtime_status: string;
  ingest_method: string;
  source_format: string;
  managed: boolean;
  code_container_id: number | null;
  copied_from_id: number | null;
  created_at: string; // ISO 8601 date string
  updated_at: string; // ISO 8601 date string
  source_type: string;
  connector_type: string;
  connector: {
    id: number;
    type: string;
    connection_type: string;
    name: string;
    description: string;
    nexset_api_compatible: boolean;
  };
  access_roles: string[];
  auto_generated: boolean;
  owner: number;
  org: number;
  data_sets: number[];
  has_template: boolean;
  vendor_endpoint: number;
  vendor: number;
  tags: string[];
  run_ids: number[];
  flow_type: string;
  ingestion_mode: string;
  vendor_id: number;
  flow_triggers: TriggerEvent[];
  archival_status?: ArchivalStatus;
}

export type Resource = Partial<IResource>;
export type Resources = Partial<IResource>[];
